import { type UseMutationResult, useMutation } from "@tanstack/react-query"
import { AxiosError, HttpStatusCode } from "axios"
import eq from "lodash-es/eq"

import { minutes, seconds } from "@/3514/utils"
import { type TEmptyAsyncCallback } from "@/shared/types/functions"

import { EAuthMutationKey } from "./auth.api.config"
import { authMutationsMutators as mutators } from "./auth.api.mutations.mutators"
import type {
    TLoginMutationPayload,
    TLoginMutationResponse,
    TPassMutationPayload,
    TPassMutationResponse,
    TSsoMutationResponse
} from "./auth.api.types"

type _TLoginMutationError = AxiosError<{ detail?: string; non_field_errors?: string[] }>
type _TLoginMutationParams = {
    onSuccess?(resp: TLoginMutationResponse, vars: TLoginMutationPayload, ctxt: void): Promise<void>
    onError?(err: _TLoginMutationError, vars: TLoginMutationPayload, ctxt: void): Promise<void>
    onSettled?: TEmptyAsyncCallback
}
type TLoginMutation = UseMutationResult<TLoginMutationResponse, _TLoginMutationError, TLoginMutationPayload>

type _TPassMutationError = AxiosError<{ password?: string }>
type _TPassMutationParams = {
    key: string
    onSuccess?(resp: TPassMutationResponse, vars: TPassMutationPayload, ctxt: void): Promise<void>
}
type TPassMutation = UseMutationResult<TPassMutationResponse, _TPassMutationError, TPassMutationPayload>

type _TSspMutationParams = {
    onSuccess?(resp: TSsoMutationResponse, vars: string, ctxt: void): Promise<void>
}
type TSsoMutation = UseMutationResult<TSsoMutationResponse, AxiosError, string>

function useLoginMutation(
    params?: _TLoginMutationParams
): ReturnType<(params: _TLoginMutationParams) => TLoginMutation> {
    return useMutation<TLoginMutationResponse, _TLoginMutationError, TLoginMutationPayload>({
        mutationKey: EAuthMutationKey.Login(),
        mutationFn: mutators.login,
        gcTime: minutes(1),
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(5),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...params
    })
}

function usePassMutation({
    key,
    ...rest
}: _TPassMutationParams): ReturnType<(params: _TPassMutationParams) => TPassMutation> {
    return useMutation<TPassMutationResponse, _TPassMutationError, TPassMutationPayload>({
        mutationKey: EAuthMutationKey.Pass(key),
        mutationFn: mutators.pass,
        gcTime: minutes(1),
        retry: (failureCount: number, error: AxiosError): boolean =>
            eq(error.status, HttpStatusCode.InternalServerError) ? failureCount <= 1 : false,
        retryDelay: seconds(5),
        networkMode: "offlineFirst",
        throwOnError: false,
        ...rest
    })
}

function useSsoMutation(params?: _TSspMutationParams): ReturnType<() => TSsoMutation> {
    return useMutation<TSsoMutationResponse, AxiosError, string>({
        mutationKey: EAuthMutationKey.SSO(),
        mutationFn: mutators.sso,
        gcTime: minutes(1),
        retry: false,
        networkMode: "offlineFirst",
        throwOnError: false,
        ...params
    })
}

export { useLoginMutation, usePassMutation, useSsoMutation }
export type { TLoginMutation, TPassMutation, TSsoMutation }
